import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import { ResponseArray, ResponseObject } from '@/types/shared'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import { getContactRoleCandidates } from '@/modules/Customer/services'
import {
    Reseller,
    ResellerOption,
    CreateResellerRequest,
    UpdateResellerRequest,
    ResellerCounts,
    ResellerCustomer,
    AddResellerCustomerRequest,
    ResellerDashboardStats,
    ResellerStats,
    ResellerService,
    ResellerInvoice,
    ResellerCandidateContact,
} from '../types'

// ============= LIST & PAGINATION =============
export const getResellersList = async (
    payload: DataGridPayload
): Promise<ResponseArray<Reseller>> => {
    // GokuDataGrid sends filters as base64 encoded JSON
    const { filters, page, page_size, q } = payload

    const filterParams: Record<string, string> = {}
    if (filters) {
        try {
            // Decode base64 first, then parse JSON
            const decoded = atob(filters)
            const parsed = JSON.parse(decoded)
            // Only include non-empty filter values
            Object.entries(parsed).forEach(([key, value]) => {
                if (value && value !== '') {
                    filterParams[key] = value as string
                }
            })
        } catch (_e) {
            // ignore parse error
        }
    }

    const params: Record<string, any> = {
        search: q || '',
        limit: page_size || 10,
        offset: ((page || 1) - 1) * (page_size || 10),
        ...filterParams,
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
        if (
            params[key] === '' ||
            params[key] === undefined ||
            params[key] === null
        ) {
            delete params[key]
        }
    })

    const response = await datacentral.get(`/resellers?${qs.stringify(params)}`)

    // Transform Results to results for GokuDataGrid compatibility
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

// The "Chờ xác nhận" tab is a worklist: a contact whose company already has a reseller is done, so
// it is dropped server-side (rows AND total together — filtering client-side would leave the grid's
// rowCount reading the unfiltered pagination.total and break paging). Reseller linkage is resolved
// at COMPANY level, and referrers.customer_id is UNIQUE, so once a company has a reseller none of
// its other tagged contacts can ever become one — hiding them all is the accurate worklist.
export const getResellerCandidates = async (
    payload: DataGridPayload
): Promise<ResponseArray<ResellerCandidateContact>> => {
    return getContactRoleCandidates({
        ...payload,
        role: 'reseller',
        exclude_linked: true,
    })
}

// ============= COUNTS =============
export const getResellerCounts = async (params?: {
    search?: string
}): Promise<ResponseObject<ResellerCounts>> => {
    const response = await datacentral.get(
        `/resellers/counts?${qs.stringify(params || {})}`
    )
    return response?.data
}

// ============= CRUD =============
export const getResellerById = async (
    id: number
): Promise<ResponseObject<Reseller>> => {
    const response = await datacentral.get(`/resellers/${id}`)
    return response?.data
}

export const createReseller = async (
    data: CreateResellerRequest
): Promise<ResponseObject<Reseller>> => {
    const response = await datacentral.post('/resellers', data)
    return response?.data
}

export const updateReseller = async ({
    reseller_id,
    payload,
}: {
    reseller_id: number
    payload: UpdateResellerRequest
}): Promise<ResponseObject<Reseller>> => {
    const response = await datacentral.put(`/resellers/${reseller_id}`, payload)
    return response?.data
}

export const deleteReseller = async (
    reseller_id: number
): Promise<ResponseObject<Reseller>> => {
    const response = await datacentral.delete(`/resellers/${reseller_id}`)
    return response?.data
}

// ============= OPTIONS DROPDOWN =============
export const getResellerOptions = async (
    payload?: DataGridPayload
): Promise<ResellerOption[]> => {
    const response = await datacentral.get<ResponseArray<ResellerOption>>(
        `/resellers/options?${qs.stringify(payload || {})}`
    )

    if (!response?.data?.results) return []

    return response?.data?.results
}

// ============= OPTIONS FOR SELECT =============
export const getResellerSelectOptions = async (
    search?: string
): Promise<{ label: string; value: number }[]> => {
    const response = await datacentral.get<ResponseArray<ResellerOption>>(
        `/resellers/options?${qs.stringify({ q: search })}`
    )

    if (!response?.data?.results) return []

    return response?.data?.results?.map((r) => ({
        label: r.reseller_name,
        value: r.reseller_id,
    }))
}

// ============= RESELLER CUSTOMERS (Phase 1.1) =============
export const getResellerCustomers = async (
    resellerId: number,
    payload?: DataGridPayload
): Promise<ResponseArray<ResellerCustomer>> => {
    const response = await datacentral.get(
        `/resellers/${resellerId}/customers?${qs.stringify(payload || {})}`
    )
    return response?.data
}

export const addResellerCustomer = async ({
    reseller_id,
    payload,
}: {
    reseller_id: number
    payload: AddResellerCustomerRequest
}): Promise<ResponseObject<ResellerCustomer>> => {
    const response = await datacentral.post(
        `/resellers/${reseller_id}/customers`,
        payload
    )
    return response?.data
}

export const removeResellerCustomer = async ({
    reseller_id,
    customer_id,
}: {
    reseller_id: number
    customer_id: number
}): Promise<ResponseObject<ResellerCustomer>> => {
    const response = await datacentral.delete(
        `/resellers/${reseller_id}/customers/${customer_id}`
    )
    return response?.data
}

// ============= RESELLER STATS (Phase 1.2) =============
export const getResellerDashboard = async (): Promise<
    ResponseObject<ResellerDashboardStats>
> => {
    const response = await datacentral.get('/resellers/dashboard')
    return response?.data
}

export const getResellerStats = async (
    resellerId: number
): Promise<ResponseObject<ResellerStats>> => {
    const response = await datacentral.get(`/resellers/${resellerId}/stats`)
    return response?.data
}

export const getResellerServices = async (
    resellerId: number,
    payload?: DataGridPayload
): Promise<ResponseArray<ResellerService>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    const response = await datacentral.get(
        `/resellers/${resellerId}/services?${qs.stringify(params)}`
    )
    return response?.data
}

export const getResellerInvoices = async (
    resellerId: number,
    payload?: DataGridPayload
): Promise<ResponseArray<ResellerInvoice>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    const response = await datacentral.get(
        `/resellers/${resellerId}/invoices?${qs.stringify(params)}`
    )
    return response?.data
}
